/** 服务端持久化的轮次摘要；旧会话没有摘要时保持未知。 */
export interface TurnSummary {
  userMessageId: string;
  durationMs: number;
  completedAt: number;
  terminalStatus: 'completed' | 'incomplete' | 'failed' | 'stopped' | 'interrupted';
  reason?: string;
}

/** 校验网络或历史数据，避免不完整摘要被当成成功。 */
function validSummary(value: unknown): value is TurnSummary {
  if (!value || typeof value !== 'object') return false;
  const s = value as TurnSummary;
  return typeof s.userMessageId === 'string' && s.userMessageId.length > 0
    && Number.isFinite(s.durationMs) && s.durationMs >= 0
    && Number.isFinite(s.completedAt) && s.completedAt > 0
    && ['completed', 'incomplete', 'failed', 'stopped', 'interrupted'].includes(s.terminalStatus);
}

/** 将实时摘要转换为与持久化一致的消息字段；无效数据返回 undefined。 */
export function turnSummaryMetadata(value: unknown): Record<string, unknown> | undefined {
  if (!validSummary(value)) return undefined;
  return {
    turnDurationMs: value.durationMs,
    turnCompletedAt: value.completedAt,
    turnTerminalStatus: value.terminalStatus,
    ...(typeof value.reason === 'string' ? { turnTerminalReason: value.reason } : {}),
  };
}

/** 读取用户消息上的权威终态，不用助手正文和时间戳推算成功。 */
export function readTurnSummary(message?: { id: string; metadata?: Record<string, unknown> } | null): TurnSummary | undefined {
  if (!message) return undefined;
  const m = message.metadata;
  const value = {
    userMessageId: message.id, durationMs: m?.turnDurationMs, completedAt: m?.turnCompletedAt,
    terminalStatus: m?.turnTerminalStatus,
    ...(typeof m?.turnTerminalReason === 'string' ? { reason: m.turnTerminalReason } : {}),
  };
  return validSummary(value) ? value : undefined;
}

/** 会话状态用于结束流式展示；只有权威 completed 才能标记成功。 */
export function terminalSessionStatus(summary: unknown): 'completed' | 'error' | 'idle' {
  if (!validSummary(summary)) return 'idle';
  return summary.terminalStatus === 'completed' ? 'completed' : summary.terminalStatus === 'failed' ? 'error' : 'idle';
}
